import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import AdvanceAmountSelection from 'features/advance/screens/registration/AdvanceAmountSelection/AdvanceAmountSelection';
import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { FeatureFlagKey } from 'services/statsig/types';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import usePinwheelFlowPageTypename, {
  PinwheelFlowPageState,
  PinwheelFlowPageStatus,
} from 'shared/hooks/pinwheel/usePinwheelFlowPageTypename/usePinwheelFlowPageTypename';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const gateHeaderOptions = {
  headerShown: true,
  headerBackVisible: true,
  header: (props: NativeStackHeaderProps) => (
    <ExpoNavigationBar
      {...props}
      options={{
        headerShown: false,
        headerBackVisible: false,
      }}
    />
  ),
};

/**
 * Where the DD check sends the customer. `TakeOutAdvanceRouter` has already
 * decided the check is required by the time these apply, so this is purely a
 * mapping of the check's outcome.
 */
export enum TakeOutAdvanceDestination {
  /** The DD check has not settled yet - hold on the loading indicator. */
  Pending = 'Pending',
  DdPrompt = 'DdPrompt',
  AmountSelection = 'AmountSelection',
  Error = 'Error',
}

const resolveDestination = (
  ddCheck: PinwheelFlowPageState | null,
): TakeOutAdvanceDestination => {
  if (ddCheck === null) {
    return TakeOutAdvanceDestination.Pending;
  }

  switch (ddCheck.status) {
    case PinwheelFlowPageStatus.DdSetupRequired:
      return TakeOutAdvanceDestination.DdPrompt;
    case PinwheelFlowPageStatus.DdSetupNotRequired:
      return TakeOutAdvanceDestination.AmountSelection;
    case PinwheelFlowPageStatus.Error:
      return TakeOutAdvanceDestination.Error;
  }
};

/**
 * Runs the Direct Deposit check and routes on its outcome.
 *
 * Only mounted once `TakeOutAdvanceRouter` has established the check is
 * required, so the query is never paused and a `null` result unambiguously
 * means "still in flight".
 *
 * The DD prompt and the error screen are real routes rather than conditional
 * renders so they get their own Expo Router segments, which is what analytics
 * derives the screen key from - sharing this route would attribute their
 * screen views and button presses to `/varo-advance/new`.
 */
const DdCheckGate = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id?: string }>();
  const { result: ddCheck } = usePinwheelFlowPageTypename();

  const destination = resolveDestination(ddCheck);
  const errorMessage =
    ddCheck?.status === PinwheelFlowPageStatus.Error
      ? ddCheck.error?.message
      : undefined;

  useEffect(() => {
    switch (destination) {
      case TakeOutAdvanceDestination.DdPrompt:
        router.replace({
          pathname: '/varo-advance/dd-prompt',
          params: { id },
        });
        return;
      case TakeOutAdvanceDestination.Error:
        router.replace({
          pathname: '/varo-advance/error',
          params: {
            subcategory: 'va_disbursement',
            error_type: 'dd_prompt_check',
            error_description: errorMessage,
            step_name: 'va_amount_selection',
          },
        });
        return;
      case TakeOutAdvanceDestination.Pending:
      case TakeOutAdvanceDestination.AmountSelection:
      default:
        return;
    }
  }, [destination, errorMessage, id, router]);

  switch (destination) {
    case TakeOutAdvanceDestination.AmountSelection:
      return <AdvanceAmountSelection />;
    // Pending waits for the check; the other two hold the indicator in place
    // for the frame between deciding to navigate and the route changing.
    case TakeOutAdvanceDestination.Pending:
    case TakeOutAdvanceDestination.DdPrompt:
    case TakeOutAdvanceDestination.Error:
    default:
      return (
        <View style={styles.container}>
          <Stack.Screen options={gateHeaderOptions} />
          <ScreenTransitionOverlay
            isLoading
            LoadingComponent={<LoadingSpinner />}
            ErrorComponent={null}
          />
        </View>
      );
  }
};

/**
 * Gates access to the take-out-advance flow (`/varo-advance/new`).
 *
 * Answers the one synchronous question - does this customer need the Direct
 * Deposit check at all - and delegates to `DdCheckGate` when they do.
 * Splitting the two keeps the asynchronous check out of the path of
 * customers who never needed it, so they reach amount selection without a
 * loading frame.
 *
 * The check lives here rather than in `AdvanceEntryPoint` so that every
 * entry point into this route, deep links included, goes through it.
 * Customers who choose to continue without Direct Deposit come back with
 * `skipDdPrompt`, which bypasses the check instead of bouncing them
 * straight back into the prompt.
 */
const TakeOutAdvanceRouter = () => {
  const { isFeatureEnabled } = useStatsig();
  const { skipDdPrompt } = useLocalSearchParams<{ skipDdPrompt?: string }>();
  const shouldSkipDdPrompt = skipDdPrompt === 'true';

  const isDayZeroDirectDepositPhase2Enabled = isFeatureEnabled(
    FeatureFlagKey.VA_DAY_0_DIRECT_DEPOSIT_PHASE_2,
  );

  const isDdCheckRequired =
    isDayZeroDirectDepositPhase2Enabled && !shouldSkipDdPrompt;

  if (!isDdCheckRequired) {
    return <AdvanceAmountSelection />;
  }

  return <DdCheckGate />;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default TakeOutAdvanceRouter;
